import React, {useCallback, useState} from "react"
import SaveIcon from "@mui/icons-material/Save";
import {IconButton} from "../../../features/management-v2/import/inputs";
import {useSiren} from "../SirenContext";
import {ErrorMessage} from "../ErrorMessage";
import {SirenMessageContent} from "../typedefs";
import {platformAPI} from "../../../api/axiosConfig";
import {isAxiosError} from "axios";
import {useNavigateWithClientID} from "../hooks/useNavigateWithClientID";
import {useGolemErrors} from "../../../lib/golem/useGolemErrors";
import {GolemText, GolemTextArea} from "../../../lib/golem/GolemField";
import {DepartmentSelectV2} from "../../../lib/departments/DepartmentSelectV2";
import {Department} from "../../../types/userManagement";
import {FieldError} from "../../../lib/golem/FieldError";

type Props = {
    mode: "create" | "edit";
    item?: SirenMessageContent;
}

export function SirenMessageContentForm({mode, item}: Props) {
    const {clientID, setToast} = useSiren()
    const {navigateWithClientID} = useNavigateWithClientID()
    const golemErrors = useGolemErrors()
    const {setFromAxiosError, clearErrors} = golemErrors

    const [name, setName] = useState(item?.name ?? "")
    const [content, setContent] = useState(item?.content ?? "")
    const [departmentID, setDepartmentID] = useState<string | null>(item?.departmentID ?? null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const submit = useCallback(async () => {
        setError(null)
        clearErrors()
        setIsLoading(true)
        try {
            if (mode === "create") {
                await platformAPI.post("api/siren/message-contents", {name, content, clientID, departmentID: departmentID || null})
                setToast("Message content created", "success")
                navigateWithClientID("/siren/message-contents")
            } else {
                await platformAPI.patch(`api/siren/message-contents/${item!.id}`, {name, content, departmentID: departmentID || null})
                setToast("Message content updated", "success")
                navigateWithClientID("/siren/message-contents")
            }
        } catch (e) {
            setFromAxiosError(e)
            if (isAxiosError(e) && e?.response?.data?.error) {
                setError(e.response.data.error)
            } else if (!isAxiosError(e) || !e?.response?.data?.errors) {
                setError("An error has occurred")
            }
        } finally {
            setIsLoading(false)
        }
    }, [name, content, departmentID, clientID, mode, item])

    return (
        <div className="bg-white shadow-md p-4 flex flex-col gap-4">
            <h1 className="text-lg font-semibold">
                {mode === "create" ? "Create Message Content" : "Edit Message Content"}
            </h1>
            <div className="flex flex-col gap-4">
                <GolemText label="Name" value={name} onChange={setName} errors={golemErrors} field="name"/>
                <GolemTextArea label="Content" value={content} onChange={setContent} errors={golemErrors} field="content"/>
                <DepartmentSelectV2 label="Department" initialID={departmentID} clientID={clientID}
                                    error={golemErrors.fieldError("departmentID")}
                                    onChange={(d: Department | undefined) => { golemErrors.clearField("departmentID"); setDepartmentID(d?.id ?? null); }}/>
                <FieldError error={golemErrors.fieldError("departmentID")}/>
            </div>
            <IconButton text="Save" loading={isLoading} onClick={submit} icon={<SaveIcon/>} color="green"/>
            <ErrorMessage text={error}/>
        </div>
    )
}
